// modules
import { useReportGenerationTenant } from "./useReportGenerationTenant";
import dayjs from "dayjs";
// components
import { Checkbox, DatePicker, Select, Space } from "antd";
import StepContainer from "components/molecules/StepContainer/StepContainer";
import Button from "components/atoms/Button/Button";
import getIcon, { IconProps } from "utils/getIcon";
import PageTitle from "components/molecules/PageTitle/PageTitle";
import Spinner from "components/atoms/Spinner/Spinner";
import { getToYearList } from "utils/getYearList";
import { Head } from "components/molecules/Head/Head";
import LinkItem from "components/atoms/LinkItem/LinkItem";

// styles
import styles from "components/organisms/ReportGenerationTenant/ReportGenerationTenant.module.css";
import theme from "styles/theme";

// Constants
import { ReportGeneration as Constants, PageTitles, getYearList, DateTimeFormats } from "constants/global";

// hooks
import { OutletItem } from "services/api/types";

const { Option } = Select;
const Icon = (props: IconProps) => getIcon({ ...props });

type ReportType = {
	type: string | null;
	selected: boolean;
};

type VoucherType = {
	type: string | null;
	selected: boolean;
};

export type ReportGenerationTenantProps = {
	outlets: Array<OutletItem>;
};

type DropdownItem = { id: string; value: string; label?: string; disabled?: boolean };

type ReportGenerationTenantPageProps = {
	reportType: ReportType;
	selectAll: boolean;
	voucherType: VoucherType;
	fromMonth: string | null;
	fromYear: number | null;
	toMonth: string | null;
	toYear: string | null;
	searchInput: string | null;
	selectedValue: string | null;
	searchSelected: boolean;
	voucherTypes: Array<DropdownItem>;
	fromError?: boolean;
	toError?: boolean;
	loading: boolean;
	voucherRedemptionStartDate: string;
	voucherRedemptionEndDate: string;
	dateRangeError: boolean;
	handleFromMonth: (value: string) => void;
	handleFromYear: (value: number) => void;
	handleToMonth: (value: string) => void;
	handleToYear: (value: string) => void;
	onChange: (value: string) => void;
	onSearch: (value: string) => void;
	handleReportType: (value: string) => void;
	handleVoucherType: (value: string) => void;
	isDownloadButtonEnabled: () => boolean | undefined;
	handleSelectAll: (value: boolean) => void;
	proceedToGenerateReport: () => void;
	handleVoucherRedemptionDate: (_type: string, _date: string) => void;
};

type ErrorMessageWrapperTypes = {
	fieldError?: boolean;
	message: string;
};

type DateErrorClasses = {
	fromError: boolean | undefined;
	toError: boolean | undefined;
	dateRangeError: boolean;
};

const ErrorMessageWrapper = ({ fieldError, message }: ErrorMessageWrapperTypes) => {
	return fieldError ? <p className={styles?.error_style}>{message}</p> : null;
};

const Loading = ({ loading }: { loading: boolean }) => {
	return loading ? <Spinner type="transparent" /> : null;
};

function dateErrorClasses({ fromError, toError, dateRangeError }: DateErrorClasses) {
	return {
		fromDateError: fromError ? styles.error : "",
		toDateError: toError ? styles.error : "",
		dateRangeErrCls: dateRangeError ? styles.error : ""
	};
}

const ReportGenerationTenant = ({ outlets }: ReportGenerationTenantProps) => {
	const componentProps: ReportGenerationTenantPageProps = useReportGenerationTenant();
	const {
		reportType,
		fromMonth,
		fromYear,
		toMonth,
		searchSelected,
		toYear,
		voucherType,
		searchInput,
		selectAll,
		voucherTypes,
		loading,
		handleFromMonth,
		fromError,
		toError,
		voucherRedemptionStartDate,
		voucherRedemptionEndDate,
		dateRangeError,
		handleFromYear,
		handleToMonth,
		handleToYear,
		handleReportType,
		onChange,
		onSearch,
		handleVoucherType,
		isDownloadButtonEnabled,
		handleSelectAll,
		proceedToGenerateReport,
		handleVoucherRedemptionDate
	} = componentProps;

	const outletsInfo = outlets;
	let stepNumber = 0;
	const fromDateCls = fromYear && fromMonth ? styles?.filled : "";
	const toDateCls = toYear && toMonth ? styles?.filled : "";
	const isFromDateSelected = fromMonth && fromYear;
	const { dateRangeErrCls, fromDateError, toDateError } = dateErrorClasses({ fromError, toError, dateRangeError });

	const getOptions = (arr: DropdownItem[]) => {
		return arr?.map((item) => (
			<Option key={item?.id} disabled={item?.disabled} value={item?.value} className={styles.option_style}>
				{item?.label || item?.value}
			</Option>
		));
	};

	const datePickerClassName = `${styles?.date_picker} ${voucherRedemptionStartDate ? styles.filled : ""}`;
	const datePickerClassName2 = `${styles?.date_picker} ${voucherRedemptionEndDate && !dateRangeError ? styles.filled : dateRangeErrCls}`;

	return (
		<div className="min-h-screen relative">
			<Loading loading={loading} />
			<PageTitle title={Constants.NEW_USER_HEADER} subTitle={Constants.NEW_USER_SUBHEADER} />
			<div className={styles?.root}>
				<Head>{PageTitles.REPORT_GENERATION}</Head>
				{outlets.length > 1 && (
					<div className="mt-[3.25rem]">
						<StepContainer
							step={++stepNumber}
							showRequiredForSubHeader={true}
							subHeader={Constants.REPORT_GENERATION_TENANT_WITH_MULTIPLE_OUTLETS.STEP_1_SUBHEADER}
							active={true}
						/>
						<div className={styles?.select_wrapper}>
							<div className="lg:flex items-center">
								<Select
									showSearch
									className={`left-nav-search ${styles.inputSearch} ${searchInput ? styles?.filled : ""}`}
									optionFilterProp="children"
									placeholder={Constants.REPORT_GENERATION_TENANT_WITH_MULTIPLE_OUTLETS.TENENT_NAME_CODE}
									onChange={onChange}
									onSearch={onSearch}
									value={searchInput}
									data-testid="search_input"
									listHeight={325}
									disabled={selectAll}
									dropdownStyle={theme.dropdownStyle}
									suffixIcon={<span className="cursor-pointer">{getIcon({ icon: "search" })}</span>}
									filterOption={(input, option) => (option?.value as unknown as string).toLowerCase().includes(input.toLowerCase())}
								>
									{outletsInfo.map((item) => {
										return (
											<Option value={`${item.outlet_name}*${item.outlet_code}`} key={item.outlet_code}>
												<LinkItem isInsideDropdown title={item.outlet_name} description={item.outlet_code} />
											</Option>
										);
									})}
								</Select>
								<p className={styles?.ortxt}>or</p>
								<Checkbox
									prefixCls={styles?.button_prefix}
									className={styles?.selectAll_button}
									data-testid="checkbox"
									onChange={(e) => handleSelectAll(e.target?.checked)}
									checked={selectAll}
								>
									<span className="w-max flex">{Constants.REPORT_GENERATION_TENANT_WITH_MULTIPLE_OUTLETS.SELECT_ALL}</span>
								</Checkbox>
							</div>
						</div>
					</div>
				)}
				<div className={`${styles?.step_wrapper} ${outlets.length > 1 ? "" : styles?.margin}`}>
					<StepContainer
						step={++stepNumber}
						subHeader={Constants.REPORT_GENERATION_TENANT_WITH_MULTIPLE_OUTLETS.STEP_2_SUBHEADER}
						showRequiredForSubHeader={true}
						active={outlets.length > 1 ? searchSelected : true}
					/>

					<div className={styles?.select_wrapper}>
						<Select
							value={reportType.type}
							className={reportType.selected ? styles?.active_select_input : styles?.select_input}
							onChange={handleReportType}
							bordered={false}
							placeholder={Constants.REPORT_TYPE_LABEL}
							disabled={outlets.length > 1 ? !searchSelected : false}
						>
							{getOptions(Constants.REPORT_TYPE)}
						</Select>
					</div>
				</div>
				<div className={styles?.step_wrapper}>
					<StepContainer step={++stepNumber} subHeader={Constants.REPORT_GENERATION_TENANT_WITH_MULTIPLE_OUTLETS.STEP_3_SUBHEADER} active={reportType.selected} />

					<div className={styles?.select_wrapper}>
						<Select
							value={!voucherType.type ? null : voucherType.type}
							className={voucherType.selected ? styles?.active_select_input : styles?.select_input}
							disabled={!reportType.selected}
							onChange={handleVoucherType}
							bordered={false}
							placeholder={Constants.VOUCHER_TYPE_LABEL}
						>
							{getOptions(voucherTypes)}
						</Select>
					</div>
				</div>
				<div className={styles?.step_wrapper}>
					<StepContainer
						showRequiredForSubHeader={true}
						step={++stepNumber}
						subHeader={
							reportType?.type === Constants.REPORT_TYPE[1].value
								? Constants.REPORT_GENERATION_TENANT_WITH_MULTIPLE_OUTLETS.STEP_4_SUBHEADER_REDEMPTION
								: Constants.REPORT_GENERATION_TENANT_WITH_MULTIPLE_OUTLETS.STEP_4_SUBHEADER_SETTLEMENT
						}
						active={reportType.selected}
					/>
					{reportType?.type === Constants.REPORT_TYPE[1].value ? (
						<>
							<Space direction="vertical" size={24}>
								<DatePicker
									disabled={!reportType?.selected}
									allowClear={false}
									className={datePickerClassName}
									placeholder={Constants.DATE_PLACEHOLDER}
									onChange={(_, dateString) => {
										handleVoucherRedemptionDate(Constants.DATE_TYPE.START_DATE, dateString);
									}}
									suffixIcon={getIcon({ icon: "calender" })}
									format={DateTimeFormats.USE_YYYY_MM_DD}
									disabledDate={(current) => {
										return current && dayjs(current as any).isAfter(dayjs().subtract(1, "day"), "day");
									}}
								/>
							</Space>
						</>
					) : (
						<>
							<div className={styles.calender_select_wrapper}>
								<Select
									className={`${styles?.custom_month} ${fromDateCls} ${fromDateError}`}
									showArrow={false}
									disabled={!reportType.selected}
									bordered={false}
									placeholder={Constants.SELECT_MONTH}
									data-testid="fromSelect"
									value={fromMonth}
									onChange={handleFromMonth}
								>
									{getOptions(Constants.MONTH)}
								</Select>
								<div className={styles?.break_point}>
									<br />
								</div>
								<Select
									className={`${styles?.custom_year} ${fromDateCls} ${fromDateError}`}
									showArrow={true}
									placeholder={Constants.SELECT_YEAR}
									disabled={!reportType.selected}
									suffixIcon={<Icon icon="calender" />}
									bordered={false}
									value={fromYear}
									onChange={handleFromYear}
								>
									{getOptions(getYearList())}
								</Select>
							</div>
							<ErrorMessageWrapper message={Constants.CANNOT_GENERATE_REPORT} fieldError={fromError} />
						</>
					)}
				</div>
				<div className={"mt-[1.25rem] w-full"}>
					<StepContainer
						step={stepNumber + 1}
						optional={true}
						subHeader={
							reportType?.type === Constants.REPORT_TYPE[1].value
								? Constants.REPORT_GENERATION_TENANT_WITH_MULTIPLE_OUTLETS.STEP_5_SUBHEADER_REDEMPTION
								: Constants.REPORT_GENERATION_TENANT_WITH_MULTIPLE_OUTLETS.STEP_5_SUBHEADER_SETTLEMENT
						}
						active={!!isFromDateSelected}
						noMaxWidth={true}
					/>
					{reportType?.type === Constants.REPORT_TYPE[1].value ? (
						<>
							<Space direction="vertical" size={24}>
								<DatePicker
									disabled={!reportType?.selected || !voucherRedemptionStartDate}
									allowClear={false}
									className={datePickerClassName2}
									placeholder={Constants.DATE_PLACEHOLDER}
									onChange={(_, dateString) => {
										handleVoucherRedemptionDate(Constants.DATE_TYPE.END_DATE, dateString);
									}}
									suffixIcon={getIcon({ icon: "calender" })}
									format={DateTimeFormats.USE_YYYY_MM_DD}
									disabledDate={(current) => {
										return current && dayjs(current as any).isBefore(dayjs(voucherRedemptionStartDate), "day");
									}}
								/>
							</Space>
							<ErrorMessageWrapper message={Constants.DATE_RANGE_ERROR} fieldError={dateRangeError} />
						</>
					) : (
						<>
							<div className={styles?.calender_select_wrapper}>
								<Select
									placeholder={Constants.SELECT_MONTH}
									className={`${styles?.custom_month} ${toDateCls} ${toDateError}`}
									showArrow={false}
									bordered={false}
									value={toMonth}
									disabled={!isFromDateSelected}
									onChange={handleToMonth}
								>
									{getOptions(Constants.MONTH)}
								</Select>
								<div className={styles?.break_point}>
									<br />
								</div>
								<Select
									className={`${styles?.custom_year} ${toDateCls} ${toDateError}`}
									showArrow={true}
									placeholder={Constants.SELECT_YEAR}
									suffixIcon={<Icon icon="calender" />}
									bordered={false}
									value={toYear}
									disabled={!isFromDateSelected}
									onChange={handleToYear}
								>
									{getOptions(getToYearList(fromYear))}
								</Select>
							</div>
							<ErrorMessageWrapper message={Constants.CANNOT_GENERATE_REPORT} fieldError={toError} />
						</>
					)}
				</div>

				<div className={styles?.btn_wrapper}>
					<Button onClick={proceedToGenerateReport} label={Constants.DOWNLOAD_REPORT} type={isDownloadButtonEnabled() ? "activeLong" : "disabledLong"} />
				</div>
			</div>
		</div>
	);
};

export default ReportGenerationTenant;
